import React from 'react';
import {
  X,
  GraduationCap,
  Moon,
  Sun,
  LogOut,
  Youtube
} from 'lucide-react';
import { useTheme } from './ThemeProvider';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  activeSection: string;
  onSectionChange: (section: string) => void;
  menuItems: { id: string; label: string; icon: React.ComponentType<{ size?: number }> }[];
  isPublic?: boolean;
  onLoginClick?: () => void;
}

export function Sidebar({
  isOpen,
  onClose,
  activeSection,
  onSectionChange,
  menuItems,
  isPublic = false,
  onLoginClick,
}: SidebarProps) {
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-72 transform transition-transform duration-300 ease-in-out z-50
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        top-0 z-50 bg-white/55 dark:bg-gray-900/90 backdrop-blur-sm shadow-md border-r border-gray-200 dark:border-gray-700 flex flex-col`}
      >
        {/* Header */}
        <div className="bg-primary p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <GraduationCap size={28} />
              <h2 className="text-xl font-bold">GCEP</h2>
            </div>
            <button
              onClick={onClose}
              className="lg:hidden p-2 rounded-lg hover:bg-[hsl(206,100%,50%)] transition duration-200"
            >
              <X size={20} />
            </button>
          </div>

          {!isPublic && user && (
            <div className="mt-4 flex items-center space-x-3">
              <div className="w-10 h-10 bg-white bg-opacity-10 rounded-full flex items-center justify-center">
                <span className="text-[hsla(191, 100%, 50%, 1.00)] font-semibold">
                  {user?.fullName?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-semibold">{user.fullName}</p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="p-4 flex-1 overflow-y-auto">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <li key={item.id}>
                  <button
                    onClick={() => {
                      onSectionChange(item.id);
                      onClose();
                    }}
                    className={`gcep-nav-link w-full flex items-center gap-2 px-4 py-2 rounded-lg transition-colors
                      ${isActive
                        ? 'bg-[hsl(197,85%,45%)] text-white shadow-md'
                        : 'text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                  >
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* YouTube Section - ABOVE FOOTER */}
        {isPublic || !user ?
          <div className="px-4 pb-3">
            <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">
              GCEP Video Guide
            </h2>
            <div className="flex flex-col space-y-2">
              <a
                href="https://youtu.be/e9738Knu4f0?si=ucyHU-pW3Efo40mr"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <Youtube size={18} className="text-red-500" />
                <span>Gujarati</span>
              </a>
              <a
                href="https://youtu.be/lRmabdiKpU4?si=GMdsm2kiJpSzgi0b"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <Youtube size={18} className="text-red-500" />
                <span>English</span>
              </a>
            </div>
          </div> : <></>
        }


        {/* Footer */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
          {/* Theme toggle */}
          <Button
            onClick={toggleTheme}
            variant="outline"
            size="sm"
            className="p-2 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
          </Button>

          {/* Auth action */}
          {isPublic || !user ? (
            <Button
              onClick={() => {
                onLoginClick?.();
                onClose();
              }}
              variant="outline"
              size="sm"
              className="p-2 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Sign In
            </Button>
          ) : (
            <Button
              onClick={logout}
              variant="outline"
              size="sm"
              className="p-2 text-red-500 border-gray-300 dark:border-gray-600 hover:bg-red-100 dark:hover:bg-red-800"
            >
              <LogOut size={16} />
            </Button>
          )}
        </div>
      </aside>
    </>
  );
}
